import React, { useState, useEffect } from 'react'
import { invoiceService, projectService, purchaseOrderService, clientService } from '../services/api'
import TableStateWrapper from '../components/TableStateWrapper'
import TablePagination from '../components/TablePagination'
import useResponsivePageSize from '../hooks/useResponsivePageSize'
import { formatAmount, formatCurrency } from '../utils/currency'
import { formatDate, formatDateForInput } from '../utils/dateFormat'
import GeneratedPdfPreviewModal from '../components/GeneratedPdfPreviewModal'
import TemplateDocumentPreviewModal from '../components/TemplateDocumentPreviewModal'
import useRolePermissions from '../hooks/useRolePermissions'
import TruncatedText from '../components/TruncatedText'
import { exportTemplatePreviewPdf } from '../utils/exportTemplatePreviewPdf'
import MultiSelectFilterDropdown from '../components/MultiSelectFilterDropdown'
import CollapsibleFilters from '../components/CollapsibleFilters'
import { getBaseUrl } from '../utils/apiConfig'

const API_BASE_URL = getBaseUrl()

export default function Invoices() {
  const { isViewer, canMutate } = useRolePermissions()
  const [invoices, setInvoices] = useState([])
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(false)
  const [tableError, setTableError] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingInvoice, setEditingInvoice] = useState(null)
  const [editFormData, setEditFormData] = useState({
    invoice_date: '',
    payment_date: '',
    invoice_amount: '',
    status: 'Unpaid',
    remarks: '',
    remarks_type: 'Outgoing',
    payment_term: ''
  })
  const [viewingPdfPath, setViewingPdfPath] = useState(null)
  const [viewingPaymentEvidencePath, setViewingPaymentEvidencePath] = useState(null)
  const [selectedProject, setSelectedProject] = useState(null)
  const [uploadingPaymentId, setUploadingPaymentId] = useState(null)
  const [showFileListModal, setShowFileListModal] = useState(false)
  const [fileListModalData, setFileListModalData] = useState({ files: [], type: '', invoice: null })
  const [viewingFileInModal, setViewingFileInModal] = useState(null)
  const [templatePreviewData, setTemplatePreviewData] = useState(null)
  const [messageModal, setMessageModal] = useState({ open: false, title: 'Notification', message: '' })
  const [confirmModal, setConfirmModal] = useState({ open: false, title: 'Confirm', message: '', resolver: null })
  const [pendingDocumentUpload, setPendingDocumentUpload] = useState(null)
  const [pendingPaymentUpload, setPendingPaymentUpload] = useState(null)
  const [formData, setFormData] = useState({
    project_id: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    payment_date: '',
    invoice_amount: '',
    progress_claim_percentage: 100,
    status: 'Unpaid',
    remarks: '',
    remarks_type: 'Incoming',
    payment_term: ''
  })
  const [items, setItems] = useState([])
  const [currentItem, setCurrentItem] = useState({
    description: '',
    price: '',
    quantity: ''
  })
  const [editItems, setEditItems] = useState([])
  const [originalEditItems, setOriginalEditItems] = useState([])
  const [editingItemId, setEditingItemId] = useState(null)
  const [editCurrentItem, setEditCurrentItem] = useState({
    description: '',
    price: '',
    quantity: ''
  })
  const [invoiceItemsPage, setInvoiceItemsPage] = useState(1)
  const { containerRef: invoiceItemsTableRef, rowsPerPage: invoiceItemsRowsPerPage } = useResponsivePageSize({ reservedHeight: 640, rowHeight: 48, minRows: 3, maxRows: 6 })
  const [editInvoiceItemsPage, setEditInvoiceItemsPage] = useState(1)
  const { containerRef: editInvoiceItemsTableRef, rowsPerPage: editInvoiceItemsRowsPerPage } = useResponsivePageSize({ reservedHeight: 640, rowHeight: 48, minRows: 3, maxRows: 6 })
  const [filters, setFilters] = useState({
    project_ids: [],
    statuses: [],
    clientNames: [],
    invoiceNumbers: [],
    poNumbers: []
  })
  const [invoicePage, setInvoicePage] = useState(1)
  const { containerRef: invoiceTableRef, rowsPerPage: invoiceRowsPerPage } = useResponsivePageSize({ reservedHeight: 500, footerHeight: 24, rowHeight: 52, minRows: 5, maxRows: 14 })

  const hasPaymentEvidencePath = (paymentEvidencePath) => {
    if (!paymentEvidencePath) return false
    return String(paymentEvidencePath)
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean)
      .length > 0
  }

  const getFileNameFromPath = (filePath) => {
    if (!filePath) return 'Unknown File'
    const parts = filePath.split('/')
    return parts[parts.length - 1] || 'Unknown File'
  }

  const getInvoicePaymentStatus = (invoice) => (
    String(invoice?.status || '').trim().toLowerCase() === 'paid'
      ? 'Paid'
      : String(invoice?.status || '').trim().toLowerCase() === 'unpaid'
        ? 'Unpaid'
        : 'Unpaid'
  )

  // Fetch invoices and projects on mount
  useEffect(() => {
    fetchInvoices()
    fetchProjects()
  }, [])

  useEffect(() => {
    const handleFocus = () => {
      fetchInvoices()
      fetchProjects()
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [])

  useEffect(() => {
    const hasPendingOutgoingInvoices = invoices.some((invoice) => {
      const invoiceType = normalizeTypeLabel(invoice?.remarks_type || invoice?.remarksType || invoice?.inv_type || invoice?.invType || invoice?.invoice_type)
      const pdfStatus = String(invoice?.pdf_generation_status || 'ready').toLowerCase()
      return invoiceType === 'Outgoing' && pdfStatus === 'pending'
    })

    if (!hasPendingOutgoingInvoices) {
      return undefined
    }

    const timer = setInterval(async () => {
      try {
        const response = await invoiceService.getAll()
        setInvoices(response.data?.data || [])
      } catch (error) {
        // PDF refresh failed silently
      }
    }, 5000)

    return () => clearInterval(timer)
  }, [invoices])

  const fetchInvoices = async () => {
    try {
      setLoading(true)
      setTableError(null)
      const response = await invoiceService.getAll()
      setInvoices(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch invoices:', error)
      setTableError(error.response?.data?.message || 'Failed to fetch invoices')
    } finally {
      setLoading(false)
    }
  }

  const fetchProjects = async () => {
    try {
      const response = await projectService.getAll()
      setProjects(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch projects:', error)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    
    if (name === 'project_id') {
      // When project is selected, fetch PO Value and auto-calculate invoice amount
      const project = projects.find(p => p.project_id === parseInt(value))
      setSelectedProject(project)
      
      // Auto-calculate invoice amount based on PO Value and progress percentage
      if (project) {
        const poValue = parseFloat(project.po_value)
        const progress = formData.progress_claim_percentage || 100
        const calculatedAmount = (poValue * progress) / 100
        setFormData(prev => ({
          ...prev,
          project_id: value,
          invoice_amount: calculatedAmount.toFixed(2)
        }))
      } else {
        setFormData(prev => ({ ...prev, [name]: value, invoice_amount: '' }))
      }
    } else if (name === 'progress_claim_percentage') {
      // When progress changes, recalculate invoice amount
      setFormData(prev => ({ ...prev, [name]: value }))
      
      if (selectedProject) {
        const poValue = parseFloat(selectedProject.po_value)
        const calculatedAmount = (poValue * value) / 100
        setFormData(prev => ({
          ...prev,
          progress_claim_percentage: value,
          invoice_amount: calculatedAmount.toFixed(2)
        }))
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  const generateLineItemId = () => Math.random().toString(36).substr(2, 9)

  const showMessage = (message, title = 'Notification') => {
    setMessageModal({ open: true, title, message: String(message || '') })
  }

  const showConfirm = (message, title = 'Confirm') => {
    return new Promise((resolve) => {
      setConfirmModal({
        open: true,
        title,
        message: String(message || ''),
        resolver: resolve
      })
    })
  }

  const handleItemChange = (e) => {
    const { name, value } = e.target
    setCurrentItem(prev => ({ ...prev, [name]: value }))
  }

  const addItem = () => {
    if (!currentItem.description || !currentItem.price || !currentItem.quantity) {
      showMessage('Please fill in all item fields', 'Validation')
      return
    }
    const amount = parseFloat(currentItem.price) * parseFloat(currentItem.quantity)
    setItems(prev => [
      ...prev,
      {
        id: generateLineItemId(),
        description: currentItem.description,
        price: parseFloat(currentItem.price),
        quantity: parseFloat(currentItem.quantity),
        amount: amount
      }
    ])
    setCurrentItem({ description: '', price: '', quantity: '' })
  }

  const removeItem = (id) => {
    setItems(prev => prev.filter(item => item.id !== id))
  }

  const getTotalAmount = () => {
    return items.reduce((total, item) => total + item.amount, 0)
  }

  const getEditTotalAmount = () => {
    return editItems.reduce((total, item) => total + item.amount, 0)
  }

  const handleEditItemChange = (e) => {
    const { name, value } = e.target
    setEditCurrentItem(prev => ({ ...prev, [name]: value }))
  }

  const addEditItem = () => {
    if (!editCurrentItem.description || !editCurrentItem.price || !editCurrentItem.quantity) {
      showMessage('Please fill in all detail item fields', 'Validation')
      return
    }

    const amount = parseFloat(editCurrentItem.price) * parseFloat(editCurrentItem.quantity)
    setEditItems(prev => [
      ...prev,
      {
        id: generateLineItemId(),
        description: editCurrentItem.description,
        price: parseFloat(editCurrentItem.price),
        quantity: parseFloat(editCurrentItem.quantity),
        amount
      }
    ])
    setEditCurrentItem({ description: '', price: '', quantity: '' })
  }

  const removeEditItem = (id) => {
    setEditItems(prev => prev.filter(item => item.id !== id))
  }

  const startEditItem = (item) => {
    setEditingItemId(item.id)
    setEditCurrentItem({
      description: item.description,
      price: item.price,
      quantity: item.quantity
    })
  }

  const saveEditItem = (id) => {
    if (!editCurrentItem.description || !editCurrentItem.price || !editCurrentItem.quantity) {
      showMessage('Please fill in all detail item fields', 'Validation')
      return
    }

    const amount = parseFloat(editCurrentItem.price) * parseFloat(editCurrentItem.quantity)
    setEditItems(prev => prev.map(item =>
      item.id === id
        ? {
            ...item,
            description: editCurrentItem.description,
            price: parseFloat(editCurrentItem.price),
            quantity: parseFloat(editCurrentItem.quantity),
            amount
          }
        : item
    ))
    setEditingItemId(null)
    setEditCurrentItem({ description: '', price: '', quantity: '' })
  }

  const getFilteredInvoices = () => {
    return invoices.filter(inv => {
      const projectId = String(inv?.project_id || '')
      const normalizedStatus = getInvoicePaymentStatus(inv)
      const clientName = getInvoiceClientName(inv)
      const invoiceNumber = String(inv?.invoice_number || '')
      const poNumber = String(inv?.PurchaseOrder?.po_number || inv?.po_number || inv?.po_number_ref || '').trim()
      const matchProject = filters.project_ids.length === 0 || filters.project_ids.includes(projectId)
      const matchStatus = filters.statuses.length === 0 || filters.statuses.includes(normalizedStatus)
      const matchClientName = filters.clientNames.length === 0 || filters.clientNames.includes(clientName)
      const matchInvoiceNumber = filters.invoiceNumbers.length === 0 || filters.invoiceNumbers.includes(invoiceNumber)
      const matchPoNumber = filters.poNumbers.length === 0 || filters.poNumbers.includes(poNumber)
      return matchProject && matchStatus && matchClientName && matchInvoiceNumber && matchPoNumber
    })
  }

  const getInvoiceClientName = (invoice) => {
    return String(
      invoice?.PurchaseOrder?.vendor_client_details
      || invoice?.company_name
      || invoice?.client_name
      || invoice?.Project?.company_name
      || ''
    ).trim()
  }

  const getUniqueClientNames = () => {
    return [...new Set(invoices.map((invoice) => getInvoiceClientName(invoice)).filter(Boolean))].sort()
  }

  const getUniqueProjectOptions = () => {
    return projects.map((project) => ({
      value: String(project.project_id),
      label: `${project.project_code} - ${project.project_name}`
    }))
  }

  const getUniqueInvoiceNumbers = () => {
    return [...new Set(invoices.map((invoice) => String(invoice?.invoice_number || '').trim()).filter(Boolean))].sort()
  }

  const getUniquePoNumbers = () => {
    return [...new Set(invoices.map((invoice) => String(invoice?.PurchaseOrder?.po_number || invoice?.po_number || invoice?.po_number_ref || '').trim()).filter(Boolean))].sort()
  }

  const filteredInvoices = getFilteredInvoices()
  const totalInvoicePages = Math.max(1, Math.ceil(filteredInvoices.length / invoiceRowsPerPage))
  const paginatedInvoices = filteredInvoices.slice((invoicePage - 1) * invoiceRowsPerPage, invoicePage * invoiceRowsPerPage)

  useEffect(() => {
    setInvoicePage(1)
  }, [filters, invoiceRowsPerPage])

  useEffect(() => {
    setInvoicePage((prev) => Math.min(prev, totalInvoicePages))
  }, [totalInvoicePages])

  const totalInvoiceItemPages = Math.max(1, Math.ceil(items.length / invoiceItemsRowsPerPage))
  const paginatedInvoiceItems = items.slice((invoiceItemsPage - 1) * invoiceItemsRowsPerPage, invoiceItemsPage * invoiceItemsRowsPerPage)
  const totalEditInvoiceItemPages = Math.max(1, Math.ceil(editItems.length / editInvoiceItemsRowsPerPage))
  const paginatedEditInvoiceItems = editItems.slice((editInvoiceItemsPage - 1) * editInvoiceItemsRowsPerPage, editInvoiceItemsPage * editInvoiceItemsRowsPerPage)

  useEffect(() => {
    setInvoiceItemsPage((prev) => Math.min(prev, totalInvoiceItemPages))
  }, [totalInvoiceItemPages])

  useEffect(() => {
    setEditInvoiceItemsPage((prev) => Math.min(prev, totalEditInvoiceItemPages))
  }, [totalEditInvoiceItemPages])

  const resetFilters = () => {
    setFilters({
      project_ids: [],
      statuses: [],
      clientNames: [],
      invoiceNumbers: [],
      poNumbers: []
    })
  }

  const openCreateModal = () => {
    setSelectedProject(null)
    setItems([])
    setCurrentItem({ description: '', price: '', quantity: '' })
    setFormData({
      project_id: '',
      invoice_number: '',
      invoice_date: new Date().toISOString().split('T')[0],
      payment_date: '',
      invoice_amount: '',
      progress_claim_percentage: 100,
      status: 'Unpaid',
      remarks: '',
      remarks_type: 'Incoming',
      payment_term: ''
    })
    setShowModal(true)
  }

  const showOutgoingInfo = () => {
    showMessage('This + New button creates standalone Incoming documents only. It does not trigger project-flow actions.', 'Information')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setLoading(true)
      
      // Validate form
      if (!formData.project_id || !formData.invoice_number || !formData.invoice_date || !formData.invoice_amount) {
        showMessage('Please fill in all required fields', 'Validation')
        return
      }

      // Count all issued (outgoing) invoices for this project, regardless of payment status.
      const projectInvoices = invoices.filter((inv) => {
        if (inv.project_id !== parseInt(formData.project_id)) return false
        const invoiceType = normalizeTypeLabel(inv?.remarks_type || inv?.remarksType || inv?.inv_type || inv?.invType || inv?.invoice_type)
        return invoiceType === 'Outgoing'
      })
      const currentTotal = projectInvoices.reduce((sum, inv) => sum + parseFloat(inv.progress_amount || inv.invoice_amount || 0), 0)
      const poValue = parseFloat(selectedProject?.po_value || 0)
      const newInvoiceAmount = parseFloat(formData.invoice_amount)
      
      // Validate total doesn't exceed PO value
      if (currentTotal + newInvoiceAmount > poValue) {
        showMessage(`Total invoiced (${formatCurrency(currentTotal + newInvoiceAmount)}) exceeds PO Value (${formatCurrency(poValue)}).\nRemaining balance: ${formatCurrency(poValue - currentTotal)}`, 'Validation')
        return
      }

      if (normalizeTypeLabel(formData.remarks_type) === 'Outgoing' && items.length === 0) {
        showMessage('Please add at least one detail item for outgoing invoice PDF generation', 'Validation')
        return
      }

      if (items.length > 0) {
        const existingItemsResponse = await projectService.getDetailItems(parseInt(formData.project_id))
        const existingItems = existingItemsResponse.data && existingItemsResponse.data.data
          ? existingItemsResponse.data.data
          : []

        for (const existingItem of existingItems) {
          if (existingItem.detail_item_id) {
            await projectService.deleteDetailItem(parseInt(formData.project_id), existingItem.detail_item_id)
          }
        }

        for (const item of items) {
          await projectService.addDetailItem(parseInt(formData.project_id), {
            description: item.description,
            price: parseFloat(item.price),
            quantity: parseFloat(item.quantity),
            amount: parseFloat(item.amount)
          })
        }
      }

      // Create invoice with Pending status
      await invoiceService.create({
        project_id: parseInt(formData.project_id),
        po_id: selectedProject?.PurchaseOrders?.[0]?.po_id || null,
        po_number_ref: selectedProject?.PurchaseOrders?.[0]?.po_number || null,
        invoice_number: formData.invoice_number,
        invoice_date: formData.invoice_date,
        payment_date: formData.payment_date || null,
        invoice_amount: newInvoiceAmount,
        progress_claim_percentage: parseInt(formData.progress_claim_percentage),
        remarks: formData.remarks || null,
        remarks_type: formData.remarks_type || null,
        payment_term: formData.payment_term || null
      })

      showMessage('Invoice created successfully!', 'Success')
      setShowModal(false)
      await fetchInvoices()
    } catch (error) {
      console.error('Failed to create invoice:', error)
      showMessage(error.response?.data?.message || 'Failed to create invoice', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const handleEditInvoice = async (invoice) => {
    const invoiceType = normalizeTypeLabel(invoice.remarks_type || invoice.remarksType || invoice.inv_type || invoice.invType || invoice.invoice_type)

    setEditingInvoice(invoice)
    setEditFormData({
      invoice_number: invoice.invoice_number || '',
      invoice_date: invoice.invoice_date ? new Date(invoice.invoice_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      payment_date: invoice.payment_date ? new Date(invoice.payment_date).toISOString().split('T')[0] : '',
      invoice_amount: invoice.invoice_amount ?? '',
      status: getInvoicePaymentStatus(invoice),
      remarks: invoice.remarks || '',
      remarks_type: invoice.remarks_type || 'Outgoing',
      payment_term: invoice.payment_term || ''
    })

    setEditItems([])
    setOriginalEditItems([])
    setEditingItemId(null)
    setEditCurrentItem({ description: '', price: '', quantity: '' })
    setEditInvoiceItemsPage(1)

    if (invoiceType === 'Outgoing' && invoice.po_id) {
      try {
        const poResponse = await purchaseOrderService.getById(invoice.po_id)
        const poData = poResponse?.data?.data || null
        const poDetailItems = parsePoDetailItems(poData?.detail_items)

        const loadedItems = poDetailItems.map((item) => ({
          id: generateLineItemId(),
          description: item.description,
          price: parseFloat(item.price),
          quantity: parseFloat(item.quantity),
          amount: parseFloat(item.amount || item.price * item.quantity)
        }))

        setEditItems(loadedItems)
        setOriginalEditItems(loadedItems)
      } catch (error) {
        // Could not load purchase order detail items for invoice edit
      }
    }

    setShowEditModal(true)
  }

  const handleEditSubmit = async (e) => {
    e.preventDefault()
    if (!editingInvoice) return

    if (!editFormData.invoice_number || editFormData.invoice_number.trim() === '') {
      showMessage('Please enter an invoice number', 'Validation')
      return
    }

    const newAmount = parseFloat(editFormData.invoice_amount)
    if (Number.isNaN(newAmount) || newAmount <= 0) {
      showMessage('Please enter a valid invoice amount', 'Validation')
      return
    }

    try {
      setLoading(true)

      if (normalizeTypeLabel(editFormData.remarks_type) === 'Outgoing' && editingInvoice.po_id) {
        const normalizedPoItems = editItems
          .map((item) => ({
            description: String(item.description || '').trim(),
            price: parseFloat(item.price || 0),
            quantity: parseFloat(item.quantity || 0)
          }))
          .filter((item) => item.description && Number.isFinite(item.price) && Number.isFinite(item.quantity) && item.quantity > 0)

        if (normalizedPoItems.length === 0) {
          showMessage('Please keep at least one PO detail item for outgoing invoice.', 'Validation')
          setLoading(false)
          return
        }

        await purchaseOrderService.update(editingInvoice.po_id, {
          detail_items: normalizedPoItems
        })
      }

      await invoiceService.update(editingInvoice.invoice_id, {
        invoice_number: editFormData.invoice_number,
        invoice_date: editFormData.invoice_date,
        payment_date: editFormData.payment_date || null,
        invoice_amount: newAmount,
        status: editFormData.status,
        remarks: editFormData.remarks || null,
        remarks_type: editFormData.remarks_type || null,
        payment_term: editFormData.payment_term || null
      })

      // Handle pending document upload
      if (pendingDocumentUpload && pendingDocumentUpload.invoiceId === editingInvoice.invoice_id) {
        try {
          const formDataToSend = new FormData()
          formDataToSend.append('file', pendingDocumentUpload.file)
          await invoiceService.uploadDocument(pendingDocumentUpload.invoiceId, formDataToSend)
          showMessage('Document uploaded successfully!', 'Success')
        } catch (error) {
          console.error('Failed to upload document:', error)
          showMessage(error.response?.data?.message || 'Failed to upload document', 'Error')
        }
        setPendingDocumentUpload(null)
      }

      // Handle pending payment evidence upload
      if (pendingPaymentUpload && pendingPaymentUpload.invoiceId === editingInvoice.invoice_id) {
        try {
          const formDataToSend = new FormData()
          formDataToSend.append('file', pendingPaymentUpload.file)
          await invoiceService.uploadPaymentEvidence(pendingPaymentUpload.invoiceId, formDataToSend)
          showMessage('Payment evidence uploaded successfully!', 'Success')
        } catch (error) {
          console.error('Failed to upload payment evidence:', error)
          showMessage(error.response?.data?.message || 'Failed to upload payment evidence', 'Error')
        }
        setPendingPaymentUpload(null)
      }

      showMessage('Invoice updated successfully!', 'Success')
      setShowEditModal(false)
      setEditingInvoice(null)
      await fetchInvoices()
    } catch (error) {
      console.error('Failed to update invoice:', error)
      showMessage(error.response?.data?.message || 'Failed to update invoice', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteInvoice = async (invoiceId) => {
    const confirmed = await showConfirm('Are you sure you want to delete this invoice?')
    if (!confirmed) {
      return
    }

    try {
      setLoading(true)
      await invoiceService.delete(invoiceId)
      showMessage('Invoice deleted successfully!', 'Success')
      await fetchInvoices()
    } catch (error) {
      console.error('Failed to delete invoice:', error)
      showMessage(error.response?.data?.message || 'Failed to delete invoice', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const handlePaymentEvidenceUpload = async (invoiceId, file) => {
    try {
      setUploadingPaymentId(invoiceId)
      const response = await invoiceService.uploadPaymentEvidence(invoiceId, file)
      
      // Check if project status was updated
      const responseData = response.data?.data || {}
      const { projectStatusUpdated, totalProgress, updatedProject } = responseData
      
      let message = 'Payment evidence uploaded successfully!'
      if (projectStatusUpdated && updatedProject) {
        const newStatus = updatedProject.status
        if (newStatus === 'Close') {
          if (totalProgress >= 100) {
            message = `✅ Payment uploaded! Invoice marked as Paid. Project status changed to Close (${totalProgress.toFixed(2)}% total progress).`
          } else {
            message = `✅ Payment uploaded! Invoice marked as Paid. Project status changed to Close.`
          }
        } else if (newStatus === 'Ongoing') {
          message = `✅ Payment uploaded! Invoice marked as Paid. Project status changed to Ongoing (${totalProgress.toFixed(2)}% progress).`
        }
      } else {
        if (totalProgress >= 100) {
          message = `✅ Payment uploaded! Invoice marked as Paid. (${totalProgress.toFixed(2)}% total progress)`
        } else {
          message = `✅ Payment uploaded! Invoice marked as Paid (${totalProgress.toFixed(2)}% progress).`
        }
      }
      
      showMessage(message, 'Success')
      await fetchInvoices()
      await fetchProjects() // Refresh projects to show updated status
    } catch (error) {
      console.error('Failed to upload payment evidence:', error)
      showMessage(error.response?.data?.message || 'Failed to upload payment evidence', 'Error')
    } finally {
      setUploadingPaymentId(null)
    }
  }

  const handleDocumentUpload = async (invoiceId, file) => {
    try {
      const formDataToSend = new FormData()
      formDataToSend.append('file', file)
      const response = await invoiceService.uploadDocument(invoiceId, formDataToSend)
      showMessage('Document uploaded successfully!', 'Success')
      await fetchInvoices()
    } catch (error) {
      console.error('Failed to upload document:', error)
      showMessage(error.response?.data?.message || 'Failed to upload document', 'Error')
    }
  }

  const openFileUploadDialog = (invoiceId, type) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.pdf,.jpg,.jpeg,.png,.doc,.docx'
    input.multiple = false
    input.onchange = async (e) => {
      const files = e.target.files
      if (files && files.length > 0) {
        const file = files[0]
        if (type === 'payment') {
          setPendingPaymentUpload({ invoiceId, file })
          showMessage('Payment evidence selected. Click "Update" to upload.', 'Info')
        } else if (type === 'document') {
          setPendingDocumentUpload({ invoiceId, file })
          showMessage('Document selected. Click "Update" to upload.', 'Info')
        }
      }
    }
    input.click()
  }

  const downloadFile = async (filePath, fileName) => {
    try {
      // Use base URL without /api for static file access
      const baseFileUrl = API_BASE_URL.replace('/api', '')
      const response = await fetch(`${baseFileUrl}${filePath}`)
      if (!response.ok) throw new Error('Download failed')
      
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName || filePath.split('/').pop() || 'file'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
      showMessage('Failed to download file', 'Error')
    }
  }

  const viewPdf = (filePath) => {
    setViewingPdfPath(`${API_BASE_URL}${filePath}`)
  }

  const handleDownloadPreviewPdf = () => {
    if (!viewingPdfPath) return
    const pathWithoutBase = viewingPdfPath.replace(API_BASE_URL, '')
    const fallbackName = `${editingInvoice?.invoice_number || 'invoice'}.pdf`
    downloadFile(pathWithoutBase, fallbackName)
  }

  const handleDownloadTemplatePreviewPdf = async (previewElement, fileName) => {
    try {
      await exportTemplatePreviewPdf(previewElement, fileName || `${templatePreviewData?.number || 'invoice'}.pdf`)
    } catch (error) {
      showMessage(error.message || 'Failed to download template preview PDF', 'Error')
      throw error
    }
  }

  const viewPaymentEvidence = (filePath) => {
    setViewingPaymentEvidencePath(`${API_BASE_URL}${filePath}`)
  }

  const toNumber = (value) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  const normalizeText = (value) => String(value || '').trim().toLowerCase()

  const openInvoiceTemplatePreview = async (invoice) => {
    try {
      let resolvedPoNumber = invoice?.PurchaseOrder?.po_number || '-'
      let resolvedPoValue = toNumber(invoice?.PurchaseOrder?.po_value)
      let resolvedPoClientName = invoice?.PurchaseOrder?.vendor_client_details || invoice?.Project?.company_name || ''
      let resolvedPaymentTerm = invoice?.payment_term || invoice?.PurchaseOrder?.payment_term || '-'

      if ((!invoice?.PurchaseOrder?.vendor_client_details || !invoice?.PurchaseOrder?.po_value || !invoice?.PurchaseOrder?.po_number) && invoice?.po_id) {
        try {
          const poResponse = await purchaseOrderService.getById(invoice.po_id)
          const poData = poResponse.data && poResponse.data.data ? poResponse.data.data : null
          if (poData) {
            resolvedPoNumber = poData.po_number || resolvedPoNumber
            resolvedPoValue = toNumber(poData.po_value) || resolvedPoValue
            resolvedPoClientName = poData.vendor_client_details || resolvedPoClientName
            if (!invoice?.payment_term) {
              resolvedPaymentTerm = poData.payment_term || resolvedPaymentTerm
            }
          }
        } catch (poError) {
          // Could not resolve purchase order details for invoice preview:', poError)
        }
      }

      let resolvedClient = null
      if (resolvedPoClientName) {
        try {
          const clientsResponse = await clientService.getAll()
          const clients = clientsResponse.data && clientsResponse.data.data ? clientsResponse.data.data : []
          const normalizedPoClientName = normalizeText(resolvedPoClientName)
          resolvedClient = clients.find((client) => normalizeText(client.client_name) === normalizedPoClientName) || null
        } catch (clientError) {
          // Could not resolve client record for invoice preview:', clientError)
        }
      }

      let detailItems = []
      if (invoice?.po_id) {
        try {
          const poResponse = await purchaseOrderService.getById(invoice.po_id)
          const poData = poResponse?.data?.data || null
          detailItems = parsePoDetailItems(poData?.detail_items)
        } catch (poDetailError) {
          // Could not resolve purchase order detail items for invoice preview:', poDetailError)
        }
      }

      let previewItems = detailItems.map((item, index) => {
        const price = toNumber(item.price)
        const qty = toNumber(item.quantity)
        return {
          id: `row-${index + 1}`,
          description: item.description || '',
          price,
          qty,
          amount: price * qty
        }
      })

      if (previewItems.length === 0) {
        const fallbackAmount = toNumber(invoice.invoice_amount)
        previewItems = [{
          id: 'fallback-1',
          description: invoice.remarks || 'Progress Claim',
          price: fallbackAmount,
          qty: 1,
          amount: fallbackAmount
        }]
      }

      const progressPercent = toNumber(invoice.progress_claim_percentage) || 100
      const poNumberForMatch = normalizeText(resolvedPoNumber)

      // Previous claims = invoices on the same PO that were issued BEFORE this one.
      const currentDate = invoice.invoice_date ? new Date(invoice.invoice_date).getTime() : null
      const previousClaimAmount = invoices
        .filter((row) => {
          if (row.invoice_id === invoice.invoice_id) return false
          const rowPoNumber = normalizeText(row?.PurchaseOrder?.po_number)
          let samePo = false
          if (poNumberForMatch && rowPoNumber) {
            samePo = rowPoNumber === poNumberForMatch
          } else if (invoice?.po_id && row?.po_id) {
            samePo = Number(row.po_id) === Number(invoice.po_id)
          }
          if (!samePo) return false
          // Only count earlier invoices (by date, falling back to id order)
          const rowTime = row.invoice_date ? new Date(row.invoice_date).getTime() : null
          if (currentDate !== null && rowTime !== null) {
            if (rowTime !== currentDate) return rowTime < currentDate
          }
          return Number(row.invoice_id) < Number(invoice.invoice_id)
        })
        .reduce((sum, row) => sum + toNumber(row.invoice_amount), 0)

      const totalPoAmount = resolvedPoValue || toNumber(invoice.invoice_amount)
      const creditNotePercent = Number(invoice.credit_note_percent || 0)
      const creditNoteAmount = Number(invoice.credit_note_amount || 0)
      // Total Claimable Amount = Invoice Amount − Credit Note Amount.
      const adjustedClaimableAmount = Math.max(
        0,
        toNumber(invoice.invoice_amount) - creditNoteAmount
      )

      setTemplatePreviewData({
        id: invoice.invoice_id,
        number: invoice.invoice_number || 'invoice',
        fileName: `${invoice.invoice_number || 'invoice'}.pdf`,
        template: 'invoice',
        title: `Invoice Preview - ${invoice.invoice_number || 'N/A'}`,
        downloadPath: invoice.pdf_path || null,
        draft: {
          companyName: 'PROBARA GIGIH TRADING (003614861-V)',
          companyAddress1: '(IMG2), JALAN SALAK TINGGI',
          companyAddress2: 'KAMPUNG JENDERAM HULU, 43800 DENGKIL, SELANGOR',
          companyAddress3: 'Email : probaragigih@gmail.com',
          clientName: resolvedPoClientName || invoice?.Project?.company_name || '-',
          registrationNo: resolvedClient?.registration_no || '-',
          addressLine1: resolvedClient?.address_line_1 || '',
          addressLine2: resolvedClient?.address_line_2 || '',
          addressLine3: resolvedClient?.address_line_3 || '',
          addressLine4: resolvedClient?.address_line_4 || '',
          invoiceNo: invoice.invoice_number || '-',
          invoiceDate: invoice.invoice_date ? new Date(invoice.invoice_date).toISOString().split('T')[0] : '-',
          poNo: resolvedPoNumber || '-',
          paymentTerm: resolvedPaymentTerm || '-',
          projectNo: invoice?.Project?.project_code || '-',
          projectName: invoice?.Project?.project_name || '-',
          notes: 'By proceeding with this payment, you agree to the terms and conditions outlined in this invoice.',
          totalPoAmount,
          creditNotePercent,
          creditNoteAmount,
          previousClaimAmount,
          progressPercent: String(progressPercent)
        },
        items: previewItems,
        totals: {
          totalAmount: adjustedClaimableAmount
        }
      })
    } catch (error) {
      console.error('Failed to load invoice template preview:', error)
      showMessage('Failed to open invoice preview', 'Error')
    }
  }

  const openFileListModal = (invoice, fileType) => {
    // Support multiple files for payment evidence (stored as comma-separated or array)
    // fileType can be 'document' or 'payment'
    let files = []
    
    if (fileType === 'document') {
      if (invoice.pdf_path) {
        files = [{ path: invoice.pdf_path, name: `${invoice.invoice_number}.pdf` }]
      }
    } else if (fileType === 'payment') {
      if (invoice.payment_evidence_path) {
        // Support multiple files: comma-separated or single file
        const paths = invoice.payment_evidence_path.includes(',') 
          ? invoice.payment_evidence_path.split(',').map(p => p.trim())
          : [invoice.payment_evidence_path]
        
        files = paths.map((path, index) => {
          // Extract filename from path (e.g., "/uploads/invoices/file-123456.pdf" -> "file-123456.pdf")
          const fileName = path.split('/').pop() || path
          return {
            path: path,
            name: paths.length > 1 ? `Payment-Evidence-${index + 1}-${fileName}` : `${fileName}`
          }
        })
      }
    }
    
    setFileListModalData({
      files: files,
      type: fileType,
      invoice: invoice
    })
    setShowFileListModal(true)
  }

  const viewFileInModal = (filePath) => {
    // For file access, we need to use the base URL without /api
    let baseFileUrl = API_BASE_URL.replace('/api', '')

    // Special handling for different environments
    if (baseFileUrl.includes('localhost')) {
      baseFileUrl = 'http://localhost:5000'
    } else if (baseFileUrl.includes('app.probaragigih.my') || baseFileUrl.includes('probaragigih.my')) {
      // In production, files should be served from the same domain as the API
      // If files don't exist on production server, this will show a 404 error
      baseFileUrl = baseFileUrl.replace('/api', '')
    }

    const fullPath = `${baseFileUrl}${filePath}`
    setShowFileListModal(false)
    if (getFileExtension(filePath) === 'pdf') {
      setViewingPdfPath(fullPath)
      return
    }
    setViewingFileInModal(fullPath)
  }

  const getFileExtension = (filePath) => {
    return filePath?.split('.').pop()?.toLowerCase() || ''
  }

  const getStatusBadge = (status) => {
    const statusColors = {
      'Paid': 'bg-green-100 text-green-800',
      'Unpaid': 'bg-red-100 text-red-800'
    }
    return statusColors[status] || 'bg-gray-100 text-gray-800'
  }

  const normalizeTypeLabel = (rawType) => {
    const normalized = String(rawType || '').trim().toLowerCase()
    if (normalized === 'outgoing') return 'Outgoing'
    if (normalized === 'incoming') return 'Incoming'
    return ''
  }

  const parsePoDetailItems = (rawValue) => {
    if (!rawValue) return []
    if (Array.isArray(rawValue)) return rawValue

    if (typeof rawValue === 'string') {
      const trimmed = rawValue.trim()
      if (!trimmed) return []
      try {
        const parsed = JSON.parse(trimmed)
        return Array.isArray(parsed) ? parsed : []
      } catch (_error) {
        return []
      }
    }

    return []
  }

  const getTypeBadge = (rawType) => {
    const typeLabel = normalizeTypeLabel(rawType)
    const typeColors = {
      'Outgoing': 'bg-amber-100 text-amber-800',
      'Incoming': 'bg-sky-100 text-sky-800'
    }
    return {
      label: typeLabel || 'N/A',
      className: typeColors[typeLabel] || 'bg-gray-100 text-gray-700'
    }
  }

  const FileIconButton = ({ onClick, icon, tooltip, disabled = false }) => {
    const [showTooltip, setShowTooltip] = useState(false)

    return (
      <div className="relative inline-block">
        <button
          onClick={onClick}
          disabled={disabled}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className="text-blue-600 hover:text-blue-800 text-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
          title={tooltip}
        >
          {icon}
        </button>
        {showTooltip && !disabled && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap pointer-events-none">
            {tooltip}
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-800 sm:text-3xl">Invoices</h1>
        <div className="flex flex-wrap items-center gap-2">
          {canMutate && (
            <button
              type="button"
              onClick={openCreateModal}
              className="relative group w-full rounded-lg bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700 sm:w-auto"
            >
              <span className="flex items-center gap-2">
                + New Invoice
                <span className="w-5 h-5 rounded-full border border-white/30 text-white text-xs flex items-center justify-center font-bold group-hover:bg-white/20 transition">?</span>
              </span>
              <span className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                Info: incoming only
              </span>
            </button>
          )}
        </div>
      </div>
      {/* Filter Section */}
      <CollapsibleFilters>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Project</label>
            <MultiSelectFilterDropdown
              options={getUniqueProjectOptions()}
              selectedValues={filters.project_ids}
              onChange={(project_ids) => setFilters((prev) => ({ ...prev, project_ids }))}
              placeholder="All projects"
              emptyText="No projects"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
            <MultiSelectFilterDropdown
              options={['Paid', 'Unpaid']}
              selectedValues={filters.statuses}
              onChange={(statuses) => setFilters((prev) => ({ ...prev, statuses }))}
              placeholder="All status"
              emptyText="No status options"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Client Name</label>
            <MultiSelectFilterDropdown
              options={getUniqueClientNames()}
              selectedValues={filters.clientNames}
              onChange={(clientNames) => setFilters((prev) => ({ ...prev, clientNames }))}
              placeholder="All clients"
              emptyText="No invoice records"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">INV No.</label>
            <MultiSelectFilterDropdown
              options={getUniqueInvoiceNumbers()}
              selectedValues={filters.invoiceNumbers}
              onChange={(invoiceNumbers) => setFilters((prev) => ({ ...prev, invoiceNumbers }))}
              placeholder="All invoice numbers"
              emptyText="No invoice numbers"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">PO No.</label>
            <MultiSelectFilterDropdown
              options={getUniquePoNumbers()}
              selectedValues={filters.poNumbers}
              onChange={(poNumbers) => setFilters((prev) => ({ ...prev, poNumbers }))}
              placeholder="All PO numbers"
              emptyText="No PO numbers"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={resetFilters}
              className="btn-reset-standard"
            >
              🔄 Reset Filters
            </button>
          </div>
        </div>
      </CollapsibleFilters>

      <TableStateWrapper
        loading={loading}
        error={tableError}
        onRetry={fetchInvoices}
        loadingText="Loading invoice records..."
        containerRef={invoiceTableRef}
      >
        <table className="w-full">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Invoice No.</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Type</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Status</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">PO No.</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Project Name</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Client Name</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Amount</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Remarks</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Payment Term</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Issued Date</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Payment Date</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Reminder</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Document</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Payment</th>
              {!isViewer && <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Action</th>}
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.length === 0 ? (
              <tr>
                <td colSpan={isViewer ? '14' : '15'} className="px-3 py-2 text-center text-gray-500">
                  No invoices yet. Create your first invoice!
                </td>
              </tr>
            ) : (
              paginatedInvoices.map((invoice) => {
                // Use project data from the invoice response (already included by API)
                const projectData = invoice.Project || {}
                const invoiceType = getTypeBadge(invoice.remarks_type || invoice.remarksType || invoice.inv_type || invoice.invType || invoice.invoice_type)
                const normalizedInvoiceType = normalizeTypeLabel(invoiceType.label)
                const invoicePaymentStatus = getInvoicePaymentStatus(invoice)
                return (
                  <tr key={invoice.invoice_id} className="border-b hover:bg-gray-50" onClick={() => handleEditInvoice(invoice)}>
                    <td className="px-3 py-2 text-sm text-left font-semibold text-blue-600 whitespace-nowrap">{invoice.invoice_number}</td>
                    <td className="px-3 py-2 text-sm text-center whitespace-nowrap">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${invoiceType.className}`}>
                        {invoiceType.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-center whitespace-nowrap">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(invoicePaymentStatus)}`}>
                        {invoicePaymentStatus}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-left text-gray-800 whitespace-nowrap"><TruncatedText text={invoice?.PurchaseOrder?.po_number || projectData?.po_no || '-'} fallback="N/A" /></td>
                    <td className="px-3 py-2 text-sm text-left whitespace-nowrap"><TruncatedText text={projectData?.project_name} fallback="N/A" /></td>
                    <td className="px-3 py-2 text-sm text-left whitespace-nowrap"><TruncatedText text={getInvoiceClientName(invoice)} fallback="N/A" /></td>
                    <td className="px-3 py-2 text-sm text-center font-semibold text-gray-800 whitespace-nowrap">
                      {formatCurrency(invoice.invoice_amount)}
                    </td>
                    <td className="px-3 py-2 text-sm text-center whitespace-nowrap"><TruncatedText text={invoice.remarks} /></td>
                    <td className="px-3 py-2 text-sm text-center whitespace-nowrap">{invoice.payment_term || '-'}</td>
                    <td className="px-3 py-2 text-sm text-center text-gray-600 whitespace-nowrap">
                      {formatDate(invoice.invoice_date)}
                    </td>
                    <td className="px-3 py-2 text-sm text-center text-gray-600 whitespace-nowrap">
                      {invoice.payment_date ? formatDate(invoice.payment_date) : '-'}
                    </td>
                    <td className="px-3 py-2 text-sm text-center whitespace-nowrap">
                      {(() => {
                        const status = String(invoice.status || '').trim().toLowerCase()
                        const isUnpaid = status !== 'paid'
                        const issuedDate = invoice.invoice_date ? new Date(invoice.invoice_date) : new Date()
                        const daysSinceIssued = (Date.now() - issuedDate.getTime()) / (1000 * 60 * 60 * 24)
                        return isUnpaid && daysSinceIssued > 30 ? (
                          <span className="text-amber-500 text-lg" title={`Unpaid for ${Math.floor(daysSinceIssued)} days`}>🔔</span>
                        ) : null
                      })()}
                    </td>
                    <td className="px-3 py-2 text-sm text-center whitespace-nowrap">
                      {normalizedInvoiceType === 'Outgoing' ? (
                        <FileIconButton
                          onClick={(e) => {
                            e.stopPropagation()
                            openInvoiceTemplatePreview(invoice)
                          }}
                          icon="✨"
                          tooltip="Open Preview"
                        />
                      ) : (
                        <>
                          {invoice.pdf_path && normalizedInvoiceType === 'Incoming' && (
                            <FileIconButton
                              onClick={(e) => {
                                e.stopPropagation()
                                openFileListModal(invoice, 'document')
                              }}
                              icon="📎"
                              tooltip="View Uploaded File"
                            />
                          )}
                          {(!invoice.pdf_path || (normalizedInvoiceType !== 'Incoming' && normalizedInvoiceType !== 'Outgoing')) && (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-center whitespace-nowrap">
                      <div className="flex gap-2 justify-center">
                        {invoice.payment_evidence_path && (
                          <FileIconButton
                            onClick={(e) => {
                              e.stopPropagation()
                              openFileListModal(invoice, 'payment')
                            }}
                            icon="📁"
                            tooltip="Files"
                          />
                        )}
                        <FileIconButton
                          onClick={(e) => {
                            e.stopPropagation()
                            openFileUploadDialog(invoice.invoice_id, 'payment')
                          }}
                          icon="📤"
                          tooltip="Upload"
                          disabled={uploadingPaymentId === invoice.invoice_id}
                        />
                      </div>
                    </td>
                    {!isViewer && (
                      <td className="px-3 py-2 text-center whitespace-nowrap">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleEditInvoice(invoice)
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium mr-3"
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteInvoice(invoice.invoice_id)
                            }}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </td>
                    )}
                  </tr>
                )
              })
            )}
          </tbody>
          {filteredInvoices.length > 0 && (
            <tfoot className="sticky bottom-0 bg-gray-100 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]">
              <tr>
                <td colSpan={6} className="px-3 py-2.5 text-right text-sm font-semibold text-gray-700 whitespace-nowrap">
                  Total ({filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''}) — Total Amount:
                </td>
                <td className="px-3 py-2.5 text-center text-sm font-bold text-gray-900 whitespace-nowrap">
                  {formatCurrency(filteredInvoices.reduce((sum, inv) => sum + (Number(inv.invoice_amount) || 0), 0))}
                </td>
                <td colSpan={isViewer ? 7 : 8} />
              </tr>
            </tfoot>
          )}
        </table>
        <TablePagination
          currentPage={invoicePage}
          totalPages={totalInvoicePages}
          totalItems={filteredInvoices.length}
          rowsPerPage={invoiceRowsPerPage}
          onPrevious={() => setInvoicePage((prev) => Math.max(prev - 1, 1))}
          onNext={() => setInvoicePage((prev) => Math.min(prev + 1, totalInvoicePages))}
        />
      </TableStateWrapper>

      {/* Create/Edit Invoice Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div className="modal-panel-standard bg-white max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6 flex items-start justify-between gap-3">
              <h2 className="text-2xl font-bold text-gray-800">Create New Invoice</h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            {(() => {
              const currentINVType = normalizeTypeLabel(formData.remarks_type)
              return (
                <div className={`mb-6 p-4 rounded-lg border ${
                  currentINVType === 'Incoming'
                    ? 'bg-blue-50 border-blue-200 text-blue-800'
                    : currentINVType === 'Outgoing'
                      ? 'bg-amber-50 border-amber-200 text-amber-800'
                      : 'bg-gray-50 border-gray-200 text-gray-700'
                }`}>
                  <p className="font-medium">
                    {currentINVType === 'Incoming'
                      ? 'Incoming invoice: document column uses uploaded PDF icon.'
                      : currentINVType === 'Outgoing'
                        ? 'Outgoing invoice: document column uses generated PDF icon.'
                        : 'Select INV type to apply document icon rules.'}
                  </p>
                </div>
              )
            })()}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project <span className="text-red-600">*</span>
                  </label>
                  <select
                    name="project_id"
                    value={formData.project_id}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">-- Select Project --</option>
                    {projects.map(project => (
                      <option key={project.project_id} value={project.project_id}>
                        {project.project_code} - {project.project_name} (PO: {formatCurrency(project.po_value)})
                      </option>
                    ))}
                  </select>
                </div>

                {selectedProject && (
                  <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">PO Value:</span> {formatCurrency(selectedProject.po_value)}
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Number <span className="text-red-600">*</span></label>
                  <input
                    type="text"
                    name="invoice_number"
                    value={formData.invoice_number}
                    onChange={handleInputChange}
                    placeholder="INV-2024-001"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Date <span className="text-red-600">*</span></label>
                  <input
                    type="date"
                    name="invoice_date"
                    value={formData.invoice_date}
                    onChange={handleInputChange}
                    required
                    className="modal-date-input px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Date</label>
                  <input
                    type="date"
                    name="payment_date"
                    value={formData.payment_date || ''}
                    onChange={handleInputChange}
                    className="modal-date-input px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invoice Amount (RM)
                    <span className="text-xs text-gray-500 ml-1">(PO Value × Progress %)</span>
                  </label>
                  <input
                    type="number"
                    name="invoice_amount"
                    value={formData.invoice_amount}
                    readOnly
                    placeholder="0.00"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Progress Claim % <span className="text-red-600">*</span></label>
                  <input
                    type="number"
                    name="progress_claim_percentage"
                    value={formData.progress_claim_percentage}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
                  <input
                    type="text"
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleInputChange}
                    placeholder="Additional remarks..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Term</label>
                  <input
                    type="text"
                    name="payment_term"
                    value={formData.payment_term}
                    onChange={handleInputChange}
                    placeholder="e.g. Net 30, COD, Due on Completion"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">INV Type</label>
                  <select
                    name="remarks_type"
                    value={formData.remarks_type}
                    onChange={handleInputChange}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                  >
                    <option value="">Select...</option>
                    <option value="Outgoing">Outgoing</option>
                    <option value="Incoming">Incoming</option>
                  </select>
                </div>
              </div>

              <div className="border-t pt-4 space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Detail Items</h3>

                <div ref={invoiceItemsTableRef} className="overflow-x-auto overflow-y-auto max-h-[70vh] border border-gray-300 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 border-b border-gray-300">
                      <tr>
                        <th className="px-2 py-1 text-center font-semibold text-gray-700 w-8 whitespace-nowrap">#</th>
                        <th className="px-2 py-1 text-left font-semibold text-gray-700 whitespace-nowrap">Description</th>
                        <th className="px-2 py-1 text-right font-semibold text-gray-700 w-24 whitespace-nowrap">Price</th>
                        <th className="px-2 py-1 text-center font-semibold text-gray-700 w-20 whitespace-nowrap">Qty</th>
                        <th className="px-2 py-1 text-right font-semibold text-gray-700 w-24 whitespace-nowrap">Amount</th>
                        {!isViewer && <th className="px-2 py-1 text-center font-semibold text-gray-700 w-24 whitespace-nowrap">Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedInvoiceItems.map((item, idx) => (
                        <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-gray-50 whitespace-nowrap">{(invoiceItemsPage - 1) * invoiceItemsRowsPerPage + idx + 1}</td>
                          <td className="px-2 py-1 whitespace-nowrap">{item.description}</td>
                          <td className="px-2 py-1 text-right whitespace-nowrap">{formatCurrency(item.price)}</td>
                          <td className="px-2 py-1 text-center whitespace-nowrap">{item.quantity.toFixed(2)}</td>
                          <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">{formatCurrency(item.amount)}</td>
                          {!isViewer && (
                            <td className="px-2 py-1 text-center">
                              <button
                                type="button"
                                onClick={() => removeItem(item.id)}
                                title="Delete item"
                                className="inline-flex items-center justify-center w-8 h-8 text-red-500 hover:text-red-600 transition"
                              >
                                ❌
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                      {!isViewer && (
                        <tr className="border-b border-gray-200 bg-blue-50">
                          <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-blue-100 whitespace-nowrap">{items.length + 1}</td>
                          <td className="px-2 py-1">
                            <textarea
                              name="description"
                              value={currentItem.description}
                              onChange={handleItemChange}
                              placeholder="Item description"
                              rows="2"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs resize-y"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="number"
                              name="price"
                              value={currentItem.price}
                              onChange={handleItemChange}
                              placeholder="0.00"
                              step="0.01"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-right"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="number"
                              name="quantity"
                              value={currentItem.quantity}
                              onChange={handleItemChange}
                              placeholder="0"
                              step="0.01"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-center"
                            />
                          </td>
                          <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">
                            {currentItem.price && currentItem.quantity
                              ? formatCurrency(parseFloat(currentItem.price) * parseFloat(currentItem.quantity))
                              : formatCurrency(0)}
                          </td>
                          <td className="px-2 py-1 text-center">
                            <button
                              type="button"
                              onClick={addItem}
                              title="Add new item"
                              className="inline-flex items-center justify-center w-8 h-8 text-green-600 hover:text-green-700 transition"
                            >
                              ➕
                            </button>
                          </td>
                        </tr>
                      )}
                      {items.length === 0 && (
                        <tr>
                          <td colSpan={isViewer ? '5' : '6'} className="px-3 py-2 text-center text-gray-500">No detail items yet.</td>
                        </tr>
                      )}
                      {items.length > 0 && (
                        <tr className="bg-blue-50 border-t-2 border-blue-300 font-bold">
                          <td className="px-2 py-1"></td>
                          <td className="px-2 py-1 text-right whitespace-nowrap">Total:</td>
                          <td className="px-2 py-1"></td>
                          <td className="px-2 py-1"></td>
                          <td className="px-2 py-1 text-right text-blue-700 whitespace-nowrap">{formatCurrency(getTotalAmount())}</td>
                          {!isViewer && <td></td>}
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <TablePagination
                    currentPage={invoiceItemsPage}
                    totalPages={totalInvoiceItemPages}
                    totalItems={items.length}
                    rowsPerPage={invoiceItemsRowsPerPage}
                    onPrevious={() => setInvoiceItemsPage((prev) => Math.max(prev - 1, 1))}
                    onNext={() => setInvoiceItemsPage((prev) => Math.min(prev + 1, totalInvoiceItemPages))}
                  />
                </div>
              </div>
              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={loading || !canMutate}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition"
                >
                  {loading ? 'Creating...' : 'Create Invoice'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg font-medium transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && editingInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowEditModal(false)}>
          <div className="modal-panel-standard bg-white max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6 flex items-start justify-between gap-3">
              <h2 className="text-2xl font-bold text-gray-800">Edit Invoice No. {editingInvoice.invoice_number}</h2>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            {(() => {
              const currentINVType = normalizeTypeLabel(editFormData.remarks_type)
              return (
                <div className={`mb-6 p-4 rounded-lg border ${
                  currentINVType === 'Incoming'
                    ? 'bg-blue-50 border-blue-200 text-blue-800'
                    : currentINVType === 'Outgoing'
                      ? 'bg-amber-50 border-amber-200 text-amber-800'
                      : 'bg-gray-50 border-gray-200 text-gray-700'
                }`}>
                  <p className="font-medium">
                    {currentINVType === 'Incoming'
                      ? 'Incoming invoice: document section shows uploaded PDF view only.'
                      : currentINVType === 'Outgoing'
                        ? 'Outgoing invoice: document section shows generated PDF view only.'
                        : 'INV type is not set. Document section follows type-based rules when type is available.'}
                  </p>
                </div>
              )
            })()}

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Invoice No. *</label>
                  <input
                    type="text"
                    value={editFormData.invoice_number}
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, invoice_number: e.target.value }))}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Name</label>
                  <input
                    type="text"
                    value={editingInvoice?.Project?.project_name || 'N/A'}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Date</label>
                  <input
                    type="date"
                    value={editFormData.invoice_date}
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, invoice_date: e.target.value }))}
                    required
                    className="modal-date-input px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Date</label>
                  <input
                    type="date"
                    value={editFormData.payment_date || ''}
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, payment_date: e.target.value }))}
                    className="modal-date-input px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Amount (RM)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editFormData.invoice_amount}
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, invoice_amount: e.target.value }))}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={editFormData.status}
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, status: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Unpaid">Unpaid</option>
                    <option value="Paid">Paid</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">INV Type</label>
                  <select
                    value={editFormData.remarks_type}
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, remarks_type: e.target.value }))}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                  >
                    <option value="">Select...</option>
                    <option value="Outgoing">Outgoing</option>
                    <option value="Incoming">Incoming</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
                <input
                  type="text"
                  value={editFormData.remarks}
                  onChange={(e) => setEditFormData((prev) => ({ ...prev, remarks: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Term</label>
                <input
                  type="text"
                  value={editFormData.payment_term}
                  onChange={(e) => setEditFormData((prev) => ({ ...prev, payment_term: e.target.value }))}
                  placeholder="e.g. Net 30, COD, Due on Completion"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {normalizeTypeLabel(editFormData.remarks_type) === 'Outgoing' && (
                <div className="border-t pt-4 space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">Detail Items</h3>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-blue-800 text-sm">
                      Edit linked PO detail items here. Clicking Update will save to the selected PO and regenerate outgoing invoice PDF using PO items.
                    </p>
                  </div>

                  <div ref={editInvoiceItemsTableRef} className="overflow-x-auto overflow-y-auto max-h-[70vh] border border-gray-300 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 border-b border-gray-300">
                        <tr>
                          <th className="px-2 py-1 text-center font-semibold text-gray-700 w-8 whitespace-nowrap">#</th>
                          <th className="px-2 py-1 text-left font-semibold text-gray-700 whitespace-nowrap">Description</th>
                          <th className="px-2 py-1 text-right font-semibold text-gray-700 w-24 whitespace-nowrap">Price</th>
                          <th className="px-2 py-1 text-center font-semibold text-gray-700 w-20 whitespace-nowrap">Qty</th>
                          <th className="px-2 py-1 text-right font-semibold text-gray-700 w-24 whitespace-nowrap">Amount</th>
                          {!isViewer && <th className="px-2 py-1 text-center font-semibold text-gray-700 w-24 whitespace-nowrap">Action</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedEditInvoiceItems.map((item, idx) => (
                          <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-gray-50 whitespace-nowrap">{(editInvoiceItemsPage - 1) * editInvoiceItemsRowsPerPage + idx + 1}</td>
                            <td className="px-2 py-1 whitespace-nowrap">{item.description}</td>
                            <td className="px-2 py-1 text-right whitespace-nowrap">{formatCurrency(item.price)}</td>
                            <td className="px-2 py-1 text-center whitespace-nowrap">{item.quantity.toFixed(2)}</td>
                            <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">{formatCurrency(item.amount)}</td>
                            {!isViewer && (
                              <td className="px-2 py-1 text-center space-x-2">
                                <button
                                  type="button"
                                  onClick={() => startEditItem(item)}
                                  className="inline-flex items-center justify-center w-8 h-8 text-blue-500 hover:text-blue-600 transition"
                                  title="Edit item"
                                >
                                  ✏️
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeEditItem(item.id)}
                                  className="inline-flex items-center justify-center w-8 h-8 text-red-500 hover:text-red-600 transition"
                                  title="Delete item"
                                >
                                  ❌
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                        {editingItemId === null && !isViewer && (
                          <tr className="border-b border-gray-200 bg-blue-50">
                            <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-blue-100 whitespace-nowrap">{editItems.length + 1}</td>
                            <td className="px-2 py-1">
                              <textarea
                                name="description"
                                value={editCurrentItem.description}
                                onChange={handleEditItemChange}
                                placeholder="Item description"
                                rows="2"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs resize-y"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <input
                                type="number"
                                name="price"
                                value={editCurrentItem.price}
                                onChange={handleEditItemChange}
                                placeholder="0.00"
                                step="0.01"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-right"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <input
                                type="number"
                                name="quantity"
                                value={editCurrentItem.quantity}
                                onChange={handleEditItemChange}
                                placeholder="0"
                                step="0.01"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-center"
                              />
                            </td>
                            <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">
                              {editCurrentItem.price && editCurrentItem.quantity
                                ? formatCurrency(parseFloat(editCurrentItem.price) * parseFloat(editCurrentItem.quantity))
                                : formatCurrency(0)}
                            </td>
                            <td className="px-2 py-1 text-center">
                              <button
                                type="button"
                                onClick={addEditItem}
                                title="Add new item"
                                className="inline-flex items-center justify-center w-8 h-8 text-green-600 hover:text-green-700 transition"
                              >
                                ➕
                              </button>
                            </td>
                          </tr>
                        )}
                        {editingItemId !== null && (
                          <tr className="border-b border-gray-200 bg-green-50">
                            <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-green-100 whitespace-nowrap">{editItems.findIndex((item) => item.id === editingItemId) + 1}</td>
                            <td className="px-2 py-1">
                              <input
                                type="text"
                                name="description"
                                value={editCurrentItem.description}
                                onChange={handleEditItemChange}
                                placeholder="Item description"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <input
                                type="number"
                                name="price"
                                value={editCurrentItem.price}
                                onChange={handleEditItemChange}
                                placeholder="0.00"
                                step="0.01"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-right"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <input
                                type="number"
                                name="quantity"
                                value={editCurrentItem.quantity}
                                onChange={handleEditItemChange}
                                placeholder="0"
                                step="0.01"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-center"
                              />
                            </td>
                            <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">
                              {editCurrentItem.price && editCurrentItem.quantity
                                ? formatCurrency(parseFloat(editCurrentItem.price) * parseFloat(editCurrentItem.quantity))
                                : formatCurrency(0)}
                            </td>
                            <td className="px-2 py-1 text-center space-x-2">
                              <button
                                type="button"
                                onClick={() => saveEditItem(editingItemId)}
                                title="Save changes"
                                className="inline-flex items-center justify-center w-8 h-8 bg-green-600 hover:bg-green-700 text-white rounded transition"
                              >
                                ✓
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingItemId(null)
                                  setEditCurrentItem({ description: '', price: '', quantity: '' })
                                }}
                                title="Cancel editing"
                                className="inline-flex items-center justify-center w-8 h-8 bg-gray-500 hover:bg-gray-600 text-white rounded transition"
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        )}
                        {editItems.length === 0 && (
                          <tr>
                            <td colSpan={isViewer ? '5' : '6'} className="px-3 py-2 text-center text-gray-500">No detail items. Add at least one item for outgoing invoice PDF.</td>
                          </tr>
                        )}
                        {editItems.length > 0 && (
                          <tr className="bg-blue-50 border-t-2 border-blue-300 font-bold">
                            <td colSpan="4" className="px-2 py-1 text-right whitespace-nowrap">Total:</td>
                            <td className="px-2 py-1 text-right text-blue-700 whitespace-nowrap">{formatCurrency(getEditTotalAmount())}</td>
                            {!isViewer && <td></td>}
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <TablePagination
                      currentPage={editInvoiceItemsPage}
                      totalPages={totalEditInvoiceItemPages}
                      totalItems={editItems.length}
                      rowsPerPage={editInvoiceItemsRowsPerPage}
                      onPrevious={() => setEditInvoiceItemsPage((prev) => Math.max(prev - 1, 1))}
                      onNext={() => setEditInvoiceItemsPage((prev) => Math.min(prev + 1, totalEditInvoiceItemPages))}
                    />
                  </div>
                </div>
              )}

              {(() => {
                const currentINVType = normalizeTypeLabel(editFormData.remarks_type)

                if (currentINVType === 'Outgoing') {
                  return (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Generated PDF</label>
                      {editingInvoice?.pdf_path ? (
                        <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-green-800 text-sm">
                            ✓ Generated file: <strong>{getFileNameFromPath(editingInvoice.pdf_path)}</strong>
                          </p>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              openInvoiceTemplatePreview(editingInvoice)
                            }}
                            className="mt-2 text-sm text-green-600 hover:text-green-800 underline font-medium"
                          >
                            Preview Generated PDF 📋
                          </button>
                        </div>
                      ) : (
                        <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <p className="text-gray-600 text-sm">No generated PDF found for this outgoing invoice.</p>
                        </div>
                      )}
                    </div>
                  )
                }

                if (currentINVType === 'Incoming') {
                  return (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Uploaded PDF</label>
                      {editingInvoice?.pdf_path ? (
                        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-blue-800 text-sm">
                            Current uploaded file: <strong>{getFileNameFromPath(editingInvoice.pdf_path)}</strong>
                          </p>
                          <button
                            type="button"
                            onClick={() => viewFileInModal(editingInvoice.pdf_path)}
                            className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline font-medium"
                          >
                            View File 📥
                          </button>
                        </div>
                      ) : (
                        <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <p className="text-gray-600 text-sm">No uploaded PDF found for this incoming invoice.</p>
                        </div>
                      )}

                      {/* Show pending document upload status */}
                      {pendingDocumentUpload && pendingDocumentUpload.invoiceId === editingInvoice.invoice_id && (
                        <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-yellow-800 text-sm">
                            📄 Pending upload: <strong>{pendingDocumentUpload.file.name}</strong>
                            <span className="ml-2 text-yellow-600">(Will be uploaded when you click Update)</span>
                          </p>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => openFileUploadDialog(editingInvoice.invoice_id, 'document')}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                      >
                        {pendingDocumentUpload && pendingDocumentUpload.invoiceId === editingInvoice.invoice_id
                          ? 'Change Document'
                          : 'Upload Document'}
                      </button>
                    </div>
                  )
                }

                return null
              })()}

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={loading || !canMutate}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <GeneratedPdfPreviewModal
        isOpen={Boolean(viewingPdfPath)}
        pdfUrl={viewingPdfPath}
        title="Invoice PDF"
        onClose={() => setViewingPdfPath(null)}
        onDownload={handleDownloadPreviewPdf}
      />

      <TemplateDocumentPreviewModal
        isOpen={Boolean(templatePreviewData)}
        template={templatePreviewData?.template}
        title={templatePreviewData?.title || 'Invoice Preview'}
        draft={templatePreviewData?.draft}
        items={templatePreviewData?.items}
        totals={templatePreviewData?.totals}
        onDownload={templatePreviewData ? handleDownloadTemplatePreviewPdf : undefined}
        onClose={() => setTemplatePreviewData(null)}
      />

      {/* Payment Evidence Viewer Modal */}
      {viewingPaymentEvidencePath && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setViewingPaymentEvidencePath(null)}>
          <div className="bg-white rounded-lg w-full h-full max-w-4xl max-h-screen overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-bold text-gray-800">Payment Evidence</h2>
              <button
                onClick={() => setViewingPaymentEvidencePath(null)}
                className="modal-close-button"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-auto flex items-center justify-center">
              {['pdf'].includes(getFileExtension(viewingPaymentEvidencePath)) ? (
                <iframe
                  src={viewingPaymentEvidencePath}
                  className="w-full h-full border-none"
                  title="Payment Evidence PDF Viewer"
                />
      ) : (
                <img
                  src={viewingPaymentEvidencePath}
                  alt="Payment Evidence"
                  className="max-w-none"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* File List Modal */}
      {showFileListModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => {
          setViewingFileInModal(null)
          setShowFileListModal(false)
        }}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-96 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-800">
                {fileListModalData.type === 'document' ? 'Documents' : 'Payment Evidence'}
              </h2>
              <button
                onClick={() => {
                  setViewingFileInModal(null)
                  setShowFileListModal(false)
                }}
                className="modal-close-button"
              >
                ×
              </button>
            </div>
            <div className="space-y-3">
              {fileListModalData.files && fileListModalData.files.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{file.name}</p>
                  </div>
                  <div className="flex gap-2 ml-2 flex-shrink-0">
                    <button
                      onClick={() => viewFileInModal(file.path)}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition"
                      title="View"
                    >
                      View
                    </button>
                    <button
                      onClick={() => downloadFile(file.path, file.name)}
                      className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition"
                      title="Download"
                    >
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* File Viewer Overlay Modal (on top of File List Modal) */}
      {viewingFileInModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setViewingFileInModal(null)}>
          <div className="bg-white rounded-lg w-full h-full max-w-4xl max-h-screen overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-bold text-gray-800">File Viewer</h2>
              <button
                onClick={() => setViewingFileInModal(null)}
                className="modal-close-button"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-auto flex items-center justify-center">
              {['pdf'].includes(getFileExtension(viewingFileInModal)) ? (
                <iframe
                  src={viewingFileInModal}
                  className="w-full h-full border-none"
                  title="File Viewer"
                />
              ) : ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(getFileExtension(viewingFileInModal)) ? (
                <img
                  src={viewingFileInModal}
                  alt="File"
                  className="max-w-none"
                />
              ) : ['doc', 'docx'].includes(getFileExtension(viewingFileInModal)) ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-600">
                  <p className="text-2xl mb-4">📄 {getFileExtension(viewingFileInModal).toUpperCase()} Document</p>
                  <p className="mb-4">Document preview not available</p>
                  <p className="text-sm text-gray-500">Please download the file to view it</p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-600">
                  <p className="text-2xl mb-4">📎 File</p>
                  <p className="mb-4">Preview not available for this file type</p>
                  <p className="text-sm text-gray-500">File type: {getFileExtension(viewingFileInModal).toUpperCase() || 'Unknown'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {messageModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" onClick={() => setMessageModal({ open: false, title: 'Notification', message: '' })}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-start justify-between gap-3">
              <h3 className="text-lg font-semibold text-gray-800">{messageModal.title}</h3>
              <button
                type="button"
                onClick={() => setMessageModal({ open: false, title: 'Notification', message: '' })}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <p className="text-gray-700 whitespace-pre-line">{messageModal.message}</p>
            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setMessageModal({ open: false, title: 'Notification', message: '' })}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" onClick={() => {
          confirmModal.resolver && confirmModal.resolver(false)
          setConfirmModal({ open: false, title: 'Confirm', message: '', resolver: null })
        }}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-start justify-between gap-3">
              <h3 className="text-lg font-semibold text-gray-800">{confirmModal.title}</h3>
              <button
                type="button"
                onClick={() => {
                  confirmModal.resolver && confirmModal.resolver(false)
                  setConfirmModal({ open: false, title: 'Confirm', message: '', resolver: null })
                }}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <p className="text-gray-700 whitespace-pre-line">{confirmModal.message}</p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => {
                  confirmModal.resolver && confirmModal.resolver(false)
                  setConfirmModal({ open: false, title: 'Confirm', message: '', resolver: null })
                }}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  confirmModal.resolver && confirmModal.resolver(true)
                  setConfirmModal({ open: false, title: 'Confirm', message: '', resolver: null })
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
